import { Search, Cloud, Users, Rocket, Zap, Shield, Brain, Globe, Target, HandCoins, Inbox, PhoneCall, Wrench, PenLine, Code2, Eye } from 'lucide-react';
import viewGame from '../../../assets/conference/view-game.png';
import viewGameV0 from '../../../assets/conference/view-game-v0.png';
import brain from '../../../assets/conference/brain.png';
import geopolitikia_architecture from '../../../assets/conference/archi.png';
import article from '../../../assets/conference/article.jpg';
import jugement from '../../../assets/conference/jugement.png';
import geminiNuclear from '../../../assets/conference/gemini_nucleair.jpg';
import claude from '../../../assets/conference/claude-color.svg';
import gemini from '../../../assets/conference/gemini-color.svg';
import banana from '../../../assets/conference/banana.svg';
import deepmind from '../../../assets/conference/deepmind-color.svg';
import antigravity from '../../../assets/conference/antigravity.jpg';
import openai from '../../../assets/conference/openai.svg';
import statistiques from '../../../assets/conference/statistiques.png';
import speaker from '../../../assets/conference/min-DSC06463fx.jpg';
import ministre from '../../../assets/conference/iaoeil_portrait.png';

/**
 * ACCENTS — la palette partagée des slides, et les trois tons du chapitre
 * « Preuve terrain ».
 *
 * POURQUOI CE BLOC EXISTE. Le chapitre « Preuve terrain » dure dix-neuf slides,
 * près d'un tiers du talk, et le bandeau affichait le même mot du début à la
 * fin. Passé les trois premières secondes, « PREUVE TERRAIN » n'apprend plus
 * rien : rien ne dit à la salle qu'on a changé de produit, et le cas nº 2 tient
 * à lui seul douze slides sans le moindre repère. Chaque cas porte donc sa
 * couleur — dans le bandeau, sur toutes ses slides, et sur sa slide d'ouverture
 * (kicker + titre).
 *
 * ⚠️ LES TROIS TEINTES EXISTENT DÉJÀ DANS LE DECK : le cyan des titres, l'ambre
 * des jauges de dette, le violet des cartes. Aucune n'a été inventée pour
 * l'occasion, et c'est délibéré — une quatrième couleur inconnue se lirait
 * « autre présentation », pas « autre sujet ». Ne pas en ajouter, ne pas les
 * saturer davantage.
 *
 * ⚠️ ET SURTOUT : ces couleurs ne sont pas décoratives. Les trois se retrouvent
 * CÔTE À CÔTE sur la slide « Où chercher le vôtre », une carte par cas. C'est là
 * qu'elles paient — trois couleurs, trois endroits, trois goulots, dans un seul
 * écran après vingt minutes de démonstration. Si cette slide perd ses `ton`, le
 * reste du dispositif ne sert plus à rien.
 *
 *  - titre : le dégradé du <h1>/<h2>, choisi par `slide.accent`
 *  - texte : le kicker, le repère de cas dans le bandeau, l'icône d'une carte
 *  - bord / halo : la carte de grille qui porte un `ton`
 */
export const ACCENTS = {
    cyan: {
        titre: 'from-blue-400 via-cyan-400 to-blue-400',
        texte: 'text-cyan-300',
        bord: 'border-cyan-400/40',
        halo: 'from-cyan-500/15 to-blue-500/10'
    },
    red: {
        titre: 'from-red-400 via-orange-400 to-red-400',
        texte: 'text-red-300',
        bord: 'border-red-400/40',
        halo: 'from-red-500/15 to-orange-500/10'
    },
    amber: {
        titre: 'from-amber-300 via-yellow-300 to-amber-300',
        texte: 'text-amber-300',
        bord: 'border-amber-400/40',
        halo: 'from-amber-500/15 to-orange-500/10'
    },
    violet: {
        titre: 'from-violet-400 via-fuchsia-400 to-violet-400',
        texte: 'text-violet-300',
        bord: 'border-violet-400/40',
        halo: 'from-violet-500/15 to-fuchsia-500/10'
    }
};

/* Toujours passer par ici : un `accent` inconnu doit rendre la slide dans la
   couleur du deck, jamais laisser une classe vide au milieu d'une conférence. */
export const accent = (nom) => ACCENTS[nom] || ACCENTS.cyan;

export const getIcon = (iconName) => {
    const icons = {
        search: <Search size={48} />,
        cloud: <Cloud size={48} />,
        users: <Users size={48} />,
        rocket: <Rocket size={48} />,
        zap: <Zap size={48} />,
        shield: <Shield size={48} />,
        brain: <Brain size={48} />,
        globe: <Globe size={48} />,
        react: "⚛️",
        python: "🐍",
        openai: "🤖",
        sql: "📊",
        copy: "🖨️",
        codex: "🪄",
        handcoins: <HandCoins size={48} />,
        // Ajoutees pour la grille « Ou chercher le votre » (slide 34) :
        // la file d'attente, l'appel qui ne produit rien, l'outillage.
        inbox: <Inbox size={48} />,
        phone: <PhoneCall size={48} />,
        wrench: <Wrench size={48} />,
        // Ajoutees pour la grille des rendements (le passage de l'usine) :
        // ce qui redige, ce qui produit, ce qui verifie. L'oeil est celui qui
        // recule -- c'est la seule tuile dont le facteur est inferieur a 1.
        penline: <PenLine size={48} />,
        code: <Code2 size={48} />,
        eye: <Eye size={48} />,
        gemini: <img src={gemini} alt="Gemini" className="w-12 h-12 inline-block object-contain" />,
        claude: <img src={claude} alt="Claude" className="w-12 h-12 inline-block object-contain" />,
        banana: <img src={banana} alt="Banana" className="w-12 h-12 inline-block object-contain" />,
        deepmind: <img src={deepmind} alt="DeepMind" className="w-12 h-12 inline-block object-contain" />,
        antigravity: <img src={antigravity} alt="Antigravity" className="w-12 h-12 inline-block object-contain" />,
        openaiImg: <img src={openai} alt="OpenAI" className="w-12 h-12 inline-block object-contain" />,
        ide: "💡"
    };
    return icons[iconName] || <Target size={48} />;
};

export const getImageMedia = (imageName) => {
    const assets = {
        geopolitikia: viewGame,
        brain: brain,
        viewGameV0: viewGameV0,
        geopolitikia_architecture: geopolitikia_architecture,
        article: article,
        jugement: jugement,
        geminiNuclear: geminiNuclear,
        statistiques: statistiques,
        speaker: speaker,
        ministre: ministre
    };

    if (assets[imageName]) {
        return <img src={assets[imageName]} alt={imageName} className="w-full h-full object-contain animate-fade-in-scale" />;
    }

    const emojis = {
        geo: "🌍",
        ai: "🤖",
        tech: "⚙️",
        challenges: "🎯",
        results: "📊",
        future: "🚀",
        react: "⚛️",
        python: "🐍",
        openai: "🤖",
        sql: "📊",
        copy: "🖨️",
        codex: "🪄",
        gemini: "🎉",
        ide: "💡"
    };
    return <div className="text-[12rem] drop-shadow-[0_0_30px_rgba(34,211,238,0.3)]">{emojis[imageName] || "✨"}</div>;
};

export const mockSimGameState = {
    joueur: {
        name: "Eurocorp",
        leader: "Adrien Giguelay",
        economy: 75,
        military: 40,
        technology: 90,
        population: 85,
        diplomacy: 55,
        natural_resources: 60,
        stability: 80
    },
    year: 2026
};
